/**
 * Resolves `principalId → display name` for the active Holder.
 *
 * - **Personal Holder**: there is exactly one Principal (the caller).
 *   The map is just that one entry, rendered as the caller's username,
 *   so e.g. "completed by jane" reads naturally.
 * - **Org Holder**: fetches the full member list via
 *   `GET /vault/holders/:id/members` and indexes by `principalId`.
 *
 * Returns a `resolve(principalId)` helper that falls back gracefully:
 * `displayName` → `@username` → a short redacted id suffix.
 *
 * The query is keyed by holderId so switching Holders refetches; the
 * cache is shared across consumers (e.g. CompletedTimelinePage and
 * any future "assigned to" affordance).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { useHolderSwitcher } from '@/components/HolderSwitcher';

export interface PrincipalNameResolver {
  resolve: (principalId: string | null | undefined) => string;
  isLoading: boolean;
}

function shortenId(id: string): string {
  // `prn_abc123...` → `prn_abc1…`. Useful when an account was deleted
  // and no display name is available.
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…`;
}

export function usePrincipalDisplayNames(): PrincipalNameResolver {
  const { user } = useAuth();
  const { selectedHolderId, selectedOption, principalId: callerPrincipalId } = useHolderSwitcher();

  const isOrg = selectedOption?.holderType === 'org';

  const { data, isLoading } = useQuery({
    queryKey: ['vault', 'holders', selectedHolderId, 'members'],
    queryFn: () => api.listVaultHolderMembers(selectedHolderId as string),
    enabled: !!selectedHolderId && isOrg,
    staleTime: 60_000,
  });

  const map = useMemo(() => {
    const m = new Map<string, string>();
    // Personal Holder: the caller is the only Principal. Use username
    // since AuthUser doesn't carry a separate displayName.
    if (!isOrg && callerPrincipalId && user?.username) {
      m.set(callerPrincipalId, user.username);
    }
    // Org Holder: index the fetched member list.
    if (isOrg && data?.members) {
      for (const member of data.members) {
        const label = member.displayName ?? member.username ?? null;
        if (label) m.set(member.principalId, label);
      }
    }
    return m;
  }, [isOrg, callerPrincipalId, user?.username, data]);

  return useMemo(
    () => ({
      resolve: (principalId: string | null | undefined) => {
        if (!principalId) return '';
        if (callerPrincipalId && principalId === callerPrincipalId) return 'You';
        return map.get(principalId) ?? shortenId(principalId);
      },
      isLoading: isOrg && isLoading,
    }),
    [map, callerPrincipalId, isOrg, isLoading],
  );
}
